/**
 * sdkApi.js - Utility functions for interacting with Trodo SDK APIs
 * Uses a non-credentialed axios instance for CORS compatibility
 */

import axios from 'axios';
import axiosInstance from 'shared/services/api/axiosInstance';

// Base URL for API calls - fallback to proxy if environment variable not set
const baseURL = process.env.REACT_APP_API_SERVER_URL || '';

// Create specialized SDK axios instance that doesn't use credentials
const sdkAxiosInstance = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: false, // Don't send credentials for SDK requests
  timeout: 30000 // 30 seconds timeout (reduced from 60 seconds)
});

/**
 * Retry function with exponential backoff
 * @param {Function} fn - Function to retry
 * @param {number} retries - Number of retries
 * @param {number} delay - Initial delay in ms
 * @returns {Promise} - Result of the function
 */
const retryWithBackoff = async (fn, retries = 2, delay = 1000) => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0 && (
      error.code === 'ECONNABORTED' || 
      error.message.includes('timeout') ||
      (error.response && error.response.status >= 500)
    )) {
      console.log(`Retrying request in ${delay}ms... (${retries} retries left)`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryWithBackoff(fn, retries - 1, delay * 2);
    }
    throw error;
  }
};

/**
 * In-flight analytics requests, keyed by site.
 *
 * AnalyticsProvider (mounted in index.js) and AnalyticsCacheProvider (mounted
 * in App.js) both fetch the same site's analytics on load, neither aware of the
 * other, so it went out twice — a real duplicate, not a StrictMode artifact.
 *
 * Deduped here rather than through the shared dedupedGet because this module
 * uses its own axios instance with `withCredentials: false` for CORS, and
 * borrowing the credentialed one to gain caching would trade a duplicate
 * request for a different request.
 *
 * In-flight only, no TTL: the callers keep their own caches, so all this has to
 * do is stop two simultaneous asks becoming two round trips.
 */
const _analyticsInFlight = new Map();

/**
 * Get analytics data for a site
 * @param {string} siteId - The site ID to fetch analytics for
 * @returns {Promise} - API response with analytics data or subscription error
 */
export const getAnalytics = async (siteId) => {
  const fetchAnalytics = async () => {
    const response = await sdkAxiosInstance.get(`/api/sdk/analytics/${siteId}`);
    return response.data;
  };

  try {
    const key = String(siteId);
    let pending = _analyticsInFlight.get(key);
    if (!pending) {
      pending = retryWithBackoff(fetchAnalytics)
        .finally(() => { _analyticsInFlight.delete(key); });
      _analyticsInFlight.set(key, pending);
    }
    const result = await pending;
    return result;
  } catch (error) {
    console.error('SDK API Error - getAnalytics:', error);
    
    // Handle timeout errors specifically
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.warn(`Analytics request timed out for site ${siteId}. This may indicate server performance issues.`);
      return {
        error: true,
        message: 'Analytics data is taking longer than expected to load. Please try again.',
        type: 'TIMEOUT'
      };
    }
    
    // Check if this is a subscription/grace period error
    if (error.response && error.response.status === 403) {
      const errorData = error.response.data;
      
      // Format and return the subscription error information
      if (errorData.error === 'Subscription required') {
        return {
          subscriptionError: true,
          message: errorData.message,
          status: errorData.subscriptionStatus,
          gracePeriod: errorData.gracePeriod || null
        };
      }
    }
    
    // Handle 408 Request Timeout from backend
    if (error.response && error.response.status === 408) {
      console.warn(`Backend timeout for site ${siteId}`);
      return {
        error: true,
        message: 'The server is experiencing high load. Analytics data may take a moment to appear.',
        type: 'SERVER_TIMEOUT'
      };
    }
    
    // Handle server errors (5xx)
    if (error.response && error.response.status >= 500) {
      return {
        error: true,
        message: 'Server error occurred while fetching analytics. Please try again later.',
        type: 'SERVER_ERROR'
      };
    }
    
    throw error;
  }
};

// Analytics endpoints removed - Overview model has been removed
// Analytics data will be computed from PostgreSQL sessions table directly

export default {
  getAnalytics
}; 